import React, { useRef } from "react";

import styled from "styled-components/macro";

import FnrButton3 from "UI/FnrButton3";
import FnrInput from "UI/FnrInput";
import { colors } from "UI/colors";

import useUpdateSetting from "../useUpdateSetting";

import validateDescription, { validatePublicName } from "./validateDescription";

export default function Description({
  description,
  publicName,
  isEditing,
  setIsEditing,
  membershipStatusID,
  canEditDescription,
  disableStatusNameInput,
  isGrayText,
  setError,
  existingPublicNames,
}) {
  const formRef = useRef();
  const descriptionInputRef = useRef();
  const publicNameInputRef = useRef();
  const { updateSetting, saving, error } = useUpdateSetting();

  async function handleSave(e) {
    let newDescription;
    let newPublicName;
    setError(null);
    if (e) {
      e.preventDefault();
    }
    try {
      // description is disabled for child nets
      // temporarily enable the description input b/c FormData ignores disabled inputs
      const descriptionIsDisabled = descriptionInputRef.current.disabled;
      if (descriptionIsDisabled) {
        descriptionInputRef.current.disabled = false;
      }

      // Get form data
      const formData = new FormData(formRef.current);

      newDescription = formData.get(`edit_description_${membershipStatusID}`);
      newPublicName = formData.get(`edit_public_name_${membershipStatusID}`);

      // re-disable so that it cannot be edited after saving
      if (descriptionIsDisabled) {
        descriptionInputRef.current.disabled = false;
      }

      // validate data
      const statusValidation = validateDescription(newDescription, "status");
      const publicNameValidation = validatePublicName({
        publicName: newPublicName,
        membershipStatusID,
        text: "description for the registration page",
        existingPublicNames,
      });

      if (!statusValidation.valid) {
        setError(statusValidation.error);
        return;
      }
      if (!publicNameValidation.valid) {
        setError(publicNameValidation.error);
        return;
      }

      await updateSetting({
        setting: "membershipStatus",
        itemID: membershipStatusID,
        value: newDescription,
        publicName: newPublicName ? newPublicName : newDescription, // if publicName is blank, use description
        action: "rename",
      });
      setError(null);
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  }

  function handleCancel() {
    descriptionInputRef.current.value = description;
    publicNameInputRef.current.value = publicName;
    setError(null);
    setIsEditing(false);
  }

  return (
    <Wrapper ref={formRef} onSubmit={handleSave} isGrayText={isGrayText}>
      {isEditing ? (
        <Stack>
          <FnrInput
            ref={descriptionInputRef}
            id={`edit_description_${membershipStatusID}`}
            name={`edit_description_${membershipStatusID}`}
            defaultValue={description}
            placeholder="Status Name"
            disabled={saving || disableStatusNameInput}
            notAllowedDisabledCursor
            autoFocus={true}
            // size="small"
            style={{
              fontSize: "14px",
              // padding: "0 0 0 4px",
              fontWeight: 400,
            }}
            onChange={(e) => {
              if (!error) return;
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSave();
                return;
              }

              if (e.key === "Escape") {
                handleCancel();
                return;
              }
            }}
          />
          <FnrInput
            ref={publicNameInputRef}
            id={`edit_public_name_${membershipStatusID}`}
            name={`edit_public_name_${membershipStatusID}`}
            defaultValue={publicName}
            placeholder="Text on Registration Page"
            disabled={saving}
            size="small"
            style={{
              fontSize: "14px",
              // padding: "0 0 0 4px",
              fontWeight: 400,
            }}
            onChange={(e) => {
              if (!error) return;
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSave();
                return;
              }

              if (e.key === "Escape") {
                handleCancel();
                return;
              }
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <FnrButton3
              onClick={handleCancel}
              disabled={saving}
              size="small"
              type="button"
            >
              Cancel
            </FnrButton3>
            <FnrButton3
              type="submit"
              color="green"
              size="small"
              loading={saving}
            >
              Save
            </FnrButton3>
          </div>
        </Stack>
      ) : (
        <div style={{ userSelect: "none" }}>
          <div>{description}</div>
          <div style={{ fontSize: "10px" }}>{publicName}</div>
        </div>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.form`
  flex: 1;
  color: ${(props) => (props.isGrayText ? colors.fnGray : "inherit")};
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid #b0b0b0;
  border-radius: 5px;
  padding-inline-end: 6px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  height: 100%;

  > input {
    border: none;
    background: transparent;
    flex: 1;
  }

  > button {
    border: none;
    background: transparent;
    padding: 0;

    &:hover {
      cursor: pointer;
    }
  }
`;
